import { Controller } from "stimulus";
import tomSelect from 'tom-select';

export default class FormController extends Controller {

    connect() {
        if (window.supercast) {
            window.supercast.trigger('pageload')
        }

        this.refreshSummary();
    }

    playlistSelectTargetConnected(el) {
        this.playlistTom = new tomSelect(el, {
            create: (input) => {
                return {
                    value: "new:" + input,
                    text: input
                }
            }
        });
    }

    removeTimestamp(e) {
        e.preventDefault();
        e.target.closest("[data-timestamp]").remove();
    }

    emailContentTargetConnected(el) {
        el.addEventListener('trix-change', (e) => {
            this.emailContentAlertTarget.dataset.email = e.target.editor.getDocument().toString();
            this.maybeWarn();
        });

        this.maybeWarn();
    }

    maybeWarn() {
        if (this.emailContentAlertTarget.dataset.description !== this.emailContentAlertTarget.dataset.email) {
            this.emailContentAlertTarget.classList.remove('hidden');
        }
    }

    togglePremiereBanner(event) {
        if (!this.hasPremiereBannerTarget) return;

        this.premiereBannerTarget.classList.toggle("hidden", !event.target.checked);
    }

    // Re-run whenever a summary line connects, so a Turbo frame swap can't leave the panel
    // describing the state the previous render was in.
    summaryLineTargetConnected() {
        this.refreshSummary();
    }

    // Recomputes the whole panel from the controls' current state rather than reacting to one
    // checkbox at a time - that's what keeps the server-rendered first paint and the client in
    // agreement. Every line's copy is already in the DOM; this only ever toggles `hidden`.
    refreshSummary() {
        const premiere = this.hasPremiereToggleTarget && this.premiereToggleTarget.checked;
        const scheduled = this.hasScheduleRadioTarget && this.scheduleRadioTarget.checked;
        const notify = this.hasNotifySelectTarget ? this.notifySelectTarget.value : null;

        // A lead only means anything for a scheduled premiere. Disabled as well as hidden so the
        // control can never submit a stale value the creator can't see.
        const leadApplies = premiere && scheduled;
        if (this.hasNotifyFieldTarget) this.notifyFieldTarget.classList.toggle("hidden", !leadApplies);
        if (this.hasNotifySelectTarget) this.notifySelectTarget.disabled = !leadApplies;
        if (this.hasNotifyImmediateNoteTarget) {
            this.notifyImmediateNoteTarget.classList.toggle("hidden", !(premiere && !scheduled));
        }

        if (!this.hasSummaryLineTarget && !this.hasSummaryEmptyTarget) return;

        const state = {
            premiere,
            email: this.hasEmailToggleTarget && this.emailToggleTarget.checked,
            discord: this.hasDiscordToggleTarget && this.discordToggleTarget.checked,
            notify,
            reminder: leadApplies && notify !== null && notify !== "off",
        };

        let announced = false;
        for (const line of this.summaryLineTargets) {
            const shown = this.summaryLineShown(line.dataset, state);
            line.classList.toggle("hidden", !shown);
            announced = announced || shown;
        }

        // The caveat rides with the reminder and is not itself a notification, so it never keeps
        // the "nothing sends" line off screen.
        if (this.hasSummaryCaveatTarget) this.summaryCaveatTarget.classList.toggle("hidden", !state.reminder);
        if (this.hasSummaryEmptyTarget) this.summaryEmptyTarget.classList.toggle("hidden", announced);
    }

    summaryLineShown(dataset, state) {
        switch (dataset.summaryLine) {
            case "reminder": return state.reminder && dataset.summaryLead === state.notify;
            case "publish_email_premiere": return state.email && state.premiere;
            case "publish_email_plain": return state.email && !state.premiere;
            case "discord": return state.discord;
            default: return false;
        }
    }
}

FormController.targets = [
    "playlistSelect",
    "emailContent",
    "emailContentAlert",
    "premiereBanner",
    "premiereToggle",
    "scheduleRadio",
    "emailToggle",
    "discordToggle",
    "notifyField",
    "notifySelect",
    "notifyImmediateNote",
    "summaryLine",
    "summaryEmpty",
    "summaryCaveat",
]
